<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LinkIcon,
  TrashIcon,
  UsersIcon,
} from '@heroicons/vue/24/solid';
import { useSharedSavedViewLibrary } from '@/composables/useSharedSavedViewLibrary';
import { usePersistentTableStore } from '@/stores/persistentTableStore';
import { useEntityStore } from '@/stores/entityStore';
import { pageLevelEntityLabel } from '@/composables/pageLevelTableEntities';
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen';

/**
 * The library of views other people have shared for this page (SAU-310).
 *
 * Opened from the "Shared views" line at the bottom of the section sidebar.
 * It is a Dialog because the list is searchable and can be long.
 *
 * Opened FROM that menu, not from a nav page: a nav page would need a page
 * grant, and page grants are navigation rather than the access-control axis for
 * data, so gating a shared data endpoint on one would be the wrong axis.
 *
 * The list is scoped to the current page's entity with no widen toggle, and the
 * server filters it by model permission — a view for a model you cannot see is
 * ABSENT, not greyed out, because the name itself carries information.
 */

const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  'update:open': [open: boolean];
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
});

const {
  search,
  sharedViews,
  isLoading,
  isError,
  isSharedViewPending,
  total,
  pageNumber,
  lastPage,
  hasNextPage,
  hasPrevPage,
  isChangingPage,
  goToPage,
  handlers,
} = useSharedSavedViewLibrary(isOpen);

// In the page only while open or fading out, so a closed dialog leaves no Teleport behind.
const { mounted: dialogMounted, onCloseFinished: onDialogClosed } = useMountedWhileOpen(isOpen);

const searchInput = ref<HTMLInputElement | null>(null);

// Focus the search box on open, and clear a stale term so reopening does not
// silently show a filtered list the user does not remember filtering.
watch(
  () => props.open,
  (open) => {
    if (!open) {
      return;
    }
    search.value = '';
    requestAnimationFrame(() => searchInput.value?.focus());
  },
);

const hasResults = computed(() => sharedViews.value.length > 0);

const store = usePersistentTableStore();
const entityStore = useEntityStore();

/** Display name for the current scope. Page-level scopes have no model behind them. */
const entityLabel = computed(() => {
  const id = store.storeKey;
  return (
    pageLevelEntityLabel(id) ??
    entityStore.entities.find((candidate) => candidate.entityId === id)?.entityDisplayName ??
    id
  );
});
</script>

<template>
  <Dialog v-if="dialogMounted" v-model:open="isOpen">
    <DialogContent class="flex max-h-[80dvh] max-w-lg flex-col gap-0 p-0" @close-auto-focus="onDialogClosed">
      <div data-testid="browse-shared-views-dialog" class="contents">
        <DialogHeader class="shrink-0 border-b px-4 py-3 text-left">
          <DialogTitle class="flex items-center gap-2 text-sm font-medium">
            <UsersIcon class="h-4 w-4 shrink-0 text-muted-foreground" />
            Shared views
          </DialogTitle>
          <DialogDescription class="text-xs text-muted-foreground">
            Views the team has shared for {{ entityLabel }}. Importing makes your own
            independent copy.
          </DialogDescription>
        </DialogHeader>

        <div class="shrink-0 px-4 pt-3">
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            placeholder="Search by view name or who shared it"
            aria-label="Search shared views by name or who shared them"
            data-testid="browse-shared-views-search"
            class="min-w-0 w-full rounded border border-input bg-background px-2 py-1 text-xs shadow-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          <div
            v-if="isLoading"
            class="flex items-center justify-center py-8"
            data-testid="browse-shared-views-loading"
          >
            <ArrowPathIcon class="h-6 w-6 animate-spin text-muted-foreground" />
          </div>

          <!-- An error state, never an empty list: "the library is empty" and
               "the library could not be reached" must not look the same. -->
          <div
            v-else-if="isError"
            class="px-2 py-8 text-center text-xs text-muted-foreground"
            data-testid="browse-shared-views-error"
          >
            Could not load shared views. Try again in a moment.
          </div>

          <div
            v-else-if="!hasResults"
            class="px-2 py-8 text-center text-xs text-muted-foreground"
            data-testid="browse-shared-views-empty"
          >
            {{
              search
                ? `No shared views match "${search}".`
                : 'Nobody has shared a view for this page yet.'
            }}
          </div>

          <template v-else>
            <!--
              The TOTAL, not this page's row count. Counting the rows in hand was
              the same number while the listing returned everything, and stopped
              being one the moment it returned a page — it would have read "50
              shared views" on every page of a library of two hundred.
            -->
            <div
              class="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
              data-testid="browse-shared-views-count"
            >
              {{ total }} shared view{{ total === 1 ? '' : 's' }}
            </div>
            <div
              v-for="sharedView in sharedViews"
              :key="sharedView.id"
              :data-testid="`browse-shared-views-row-${sharedView.id}`"
              class="group/sharedviewrow relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground focus-within:bg-accent focus-within:text-accent-foreground"
            >
              <div class="min-w-0 flex-1">
                <div class="truncate">{{ sharedView.name }}</div>
                <div class="truncate text-xs text-muted-foreground">
                  {{ sharedView.shared_by_name || 'Unknown' }}
                </div>
              </div>

              <!--
                Copy-link and delete are ROLLOVER, matching the saved-view menu
                this dialog is modelled on: they appear on the row you are on
                rather than on all of them at once. Import is the reason the
                dialog exists, so it stays put — and e2e clicks it directly.

                Opacity rather than v-if, so the row does not reflow under the
                pointer and a click still lands. `pointer-coarse` is what covers
                touch, since Tailwind v4's hover variant never fires there.

                `group-focus-within` works the same way here as in the
                saved-view menu, by a simpler route: these rows are plain divs in
                a dialog, so Tab reaches the buttons on its own. The menu needs
                `useMenuRowActionKeys` to get there, because reka cancels Tab
                inside it.

                ORDER: delete first, copy-link second. Delete is the conditional
                one — it depends on who published the entry — so putting it last
                slid the link sideways from row to row and you had to re-aim for
                a button that had not changed. Link last also matches the
                saved-view menu, where the share button is the last of the three.

                The row's highlight is the accent pair the DropdownMenuItem uses,
                on BOTH hover and focus-within, and with no transition — the menu
                snaps rather than fades. Each surface previously had exactly the
                selector the other was missing: the menu lit on focus, this lit on
                hover, so tabbing here made the action buttons fade in on a row
                that never lit up.
              -->
              <div
                class="flex shrink-0 items-center gap-2 opacity-0 transition-opacity group-hover/sharedviewrow:opacity-100 group-focus-within/sharedviewrow:opacity-100 pointer-coarse:opacity-100"
              >
                <button
                  v-if="sharedView.can_delete"
                  type="button"
                  class="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                  :title="`Remove &quot;${sharedView.name}&quot; from the library`"
                  :data-testid="`browse-shared-views-delete-${sharedView.id}`"
                  :disabled="isSharedViewPending(sharedView.id)"
                  @click.stop="handlers.onDelete(sharedView)"
                >
                  <TrashIcon class="h-3.5 w-3.5" />
                </button>

                <button
                  type="button"
                  class="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  :title="`Copy share link for &quot;${sharedView.name}&quot;`"
                  :data-testid="`browse-shared-views-copy-${sharedView.id}`"
                  @click.stop="handlers.onCopyLink(sharedView)"
                >
                  <LinkIcon class="h-3.5 w-3.5" />
                </button>
              </div>

              <button
                type="button"
                class="rounded border border-input px-2 py-0.5 text-xs shadow-sm hover:bg-accent disabled:opacity-50"
                :data-testid="`browse-shared-views-import-${sharedView.id}`"
                :disabled="isSharedViewPending(sharedView.id)"
                @click.stop="handlers.onImport(sharedView)"
              >
                {{ isSharedViewPending(sharedView.id) ? 'Importing…' : 'Import' }}
              </button>
            </div>
          </template>
        </div>

        <!--
          Prev/Next only. The shared pager can also jump to a LAST page, which the
          Elasticsearch-backed lists serve with a reverse-sort trick; this endpoint
          is a plain MySQL cursor and has none, so that button is not rendered
          rather than rendered and wrong. Hidden entirely for a library that fits
          on one page, which is nearly all of them.
        -->
        <div
          v-if="!isLoading && !isError && (hasPrevPage || hasNextPage)"
          class="flex shrink-0 items-center justify-between gap-2 border-t px-3 py-2"
          data-testid="browse-shared-views-pager"
        >
          <span class="text-xs text-muted-foreground">
            Page {{ pageNumber }}<template v-if="lastPage > 1"> of {{ lastPage }}</template>
          </span>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="flex items-center gap-1 rounded border border-input px-2 py-0.5 text-xs shadow-sm hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
              :disabled="!hasPrevPage || isChangingPage"
              data-testid="browse-shared-views-prev"
              @click="goToPage('prev')"
            >
              <ChevronLeftIcon class="h-3.5 w-3.5" />
              Prev
            </button>
            <button
              type="button"
              class="flex items-center gap-1 rounded border border-input px-2 py-0.5 text-xs shadow-sm hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
              :disabled="!hasNextPage || isChangingPage"
              data-testid="browse-shared-views-next"
              @click="goToPage('next')"
            >
              Next
              <ChevronRightIcon class="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
